'use client'

import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { UserType } from '@/types/UserType'
import { clearAuthStorage } from '@/services/storage'
import {
  registerLogoutHandler,
  unregisterLogoutHandler,
} from '@/services/auth-events'

interface AuthContextType {
  user: UserType | null
  isAuthenticated: boolean
  isLoading: boolean
  setAuthUser: (user: UserType | null) => void
  clearAuth: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<UserType | null>(null)
  const isLoading = false

  const setAuthUser = useCallback((nextUser: UserType | null) => {
    setUserState(nextUser)
  }, [])

  const clearAuth = useCallback(() => {
    setUserState(null)
    if (typeof window !== 'undefined') {
      clearAuthStorage()
    }
  }, [])

  useEffect(() => {
    const handleAuthLogout = () => {
      clearAuth()
    }

    registerLogoutHandler(handleAuthLogout)

    return () => {
      unregisterLogoutHandler(handleAuthLogout)
    }
  }, [clearAuth])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: Boolean(user),
        setAuthUser,
        clearAuth,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function useSetUser() {
  const { setAuthUser } = useAuth()
  return setAuthUser
}
